/**
 * Catalogue des formules — la SEULE source de vérité côté client.
 *
 * `PlansV2` (`/plans`), l'inscription et la carte « Ton plan » des
 * Paramètres lisent tous ce module : la vitrine et la surface applicative
 * ne peuvent donc pas afficher deux prix différents pour la même formule.
 * Si un palier ou une promesse change, il change ICI.
 *
 * Miroir exact de `backend/constants/billing.py` (les prix) et de
 * `backend/constants/units.py` (ce que contient un palier). Les montants
 * affichés ici ne facturent rien : Stripe facture d'après ses price IDs.
 *
 * Structure :
 *   TIERS            — les trois paliers (miroir de l'enum backend)
 *   PRICE_EUR        — le prélèvement MENSUEL, sans engagement
 *   PRICE_ANNUAL_EUR — le montant débité EN UNE FOIS, une fois par an
 *   PRICE_ANNUAL_PER_MONTH_EUR — ce qu'on affiche en gros en vue annuelle
 *   OVERAGE_EUR      — les actes, au-delà de ce que le plan contient
 *   HIGHLIGHT_TIER   — qui porte le badge « Recommandé »
 *   IDENTITY_COPY / INHERITANCE_COPY / FEATURES — la copy par langue
 *
 * NE PAS forker ces dicts ailleurs. Les importer.
 */

// ─────────────────────────────────────────────────────────────────────
// Paliers + prix
// ─────────────────────────────────────────────────────────────────────
//
// ULTIMATE A DISPARU. Trois paliers, pas quatre : un quatrième palier
// qui vendait « illimité » et « réponse en 2 h » promettait ce qu'une
// équipe de juristes freelances ne peut pas tenir, et brouillait le seul
// choix qui compte — un dossier mené, ou deux.
export const TIERS = ['free', 'starter', 'pro'];

/** Le prélèvement MENSUEL, sans engagement. */
export const PRICE_EUR = {
  free:    0,
  starter: 39,
  pro:     79,
};

/**
 * Le montant réellement DÉBITÉ en cycle annuel — une seule fois, pour
 * douze mois. Ce n'est pas « dix mois payés pour douze » : c'est une
 * remise en échange d'un engagement de douze mois.
 */
export const PRICE_ANNUAL_EUR = {
  free:    0,
  starter: 408,
  pro:     828,
};

/**
 * L'équivalent mensuel affiché en gros dans la vue annuelle.
 *
 * SAISI et non calculé, à dessein : 408/12 vaut exactement 34, mais un
 * futur prix qui ne tomberait pas rond afficherait « 34,33 € » là où le
 * commercial a promis 34. `annualPricesAreCoherent()` garantit que ce
 * chiffre et le montant débité se répondent au centime — un prix d'appel
 * qui aurait dérivé de la facture, c'est un litige, pas un arrondi.
 */
export const PRICE_ANNUAL_PER_MONTH_EUR = {
  free:    0,
  starter: 34,
  pro:     69,
};

/**
 * Les ACTES — ce qu'on paie au-delà de ce que le plan contient. Chacun
 * est TOUJOURS confirmé par le client avant le débit ; aucun ne part
 * tout seul. Miroir de `backend/constants/units.OVERAGE_PRICES_EUR`.
 */
export const OVERAGE_EUR = {
  analysis:       3,     // 1 analyse complète de plus — litige OU contrat
  letterOneshot:  19,    // 1 lettre signée par un juriste, sans suivi
  juristCase:     49,    // 1 dossier juriste au-delà des slots inclus
  registeredMail: 9.99,  // supplément d'ENVOI — JAMAIS inclus, nulle part
};

// Starter porte le badge : c'est le palier où l'on passe de « savoir » à
// « quelqu'un se bat pour toi », et c'est ce pas-là qu'on veut rendre
// évident. Le mettre sur Pro poussait vers le plan cher un client qui
// n'a qu'un seul litige.
export const HIGHLIGHT_TIER = 'starter';

// ─────────────────────────────────────────────────────────────────────
// Cycle de facturation — mensuel ou annuel
// ─────────────────────────────────────────────────────────────────────
//
// Miroir exact de `backend/constants/billing.py` : mêmes identifiants.
// Toute surface qui laisse CHOISIR un cycle lit ces valeurs — aucune ne
// redéclare « monthly » / « annual » dans son coin, sinon un jour l'une
// des deux enverra « annuel » au backend, qui refuse toute valeur
// inconnue.
export const BILLING_CYCLES = ['monthly', 'annual'];

/**
 * ANNUEL par défaut à l'écran (décision grille v3, 28/08/2026).
 *
 * La bascule de /plans et de /signup arrive sur l'annuel : le prix
 * d'entrée annoncé partout est « dès 34 €/mois », et l'écran d'arrivée
 * doit montrer ce prix-là. Rien n'est coché pour autant : « Choisir »
 * n'ouvre que l'étape de confirmation, qui affiche le montant réellement
 * débité (408 € / 828 €, une fois par an) et l'engagement de douze mois
 * AVANT tout paiement — le consentement se donne là, pas au chargement.
 *
 * Le défaut de DÉBIT, lui, reste MENSUEL côté serveur
 * (`backend/constants/billing.py`) : le formulaire envoie toujours son
 * cycle explicitement, et si le champ manquait en route, un client ne
 * doit jamais se réveiller engagé douze mois par défaut. Les deux défauts
 * divergent donc à dessein — l'un vend, l'autre protège.
 */
export const DEFAULT_BILLING_CYCLE = 'annual';

/** Normalise n'importe quelle entrée (URL, prop, stockage) en un cycle valide. */
export function normalizeBillingCycle(raw) {
  const c = String(raw || '').toLowerCase();
  return BILLING_CYCLES.includes(c) ? c : DEFAULT_BILLING_CYCLE;
}

/** Le montant DÉBITÉ pour un palier dans un cycle donné. */
export function priceForCycle(tier, cycle) {
  return (normalizeBillingCycle(cycle) === 'annual' ? PRICE_ANNUAL_EUR : PRICE_EUR)[tier] ?? 0;
}

/**
 * L'équivalent mensuel d'un abonnement annuel.
 *
 * C'était une DIVISION ; c'est désormais une lecture du chiffre annoncé
 * (cf. `PRICE_ANNUAL_PER_MONTH_EUR`). Le nom ne change pas parce que les
 * appelants demandent bien la même chose : « combien ça fait par mois ».
 */
export function annualPerMonthEur(tier) {
  return PRICE_ANNUAL_PER_MONTH_EUR[tier] ?? 0;
}

/** Le montant AFFICHÉ EN GROS pour un palier dans un cycle donné. */
export function displayPriceForCycle(tier, cycle) {
  return normalizeBillingCycle(cycle) === 'annual'
    ? (PRICE_ANNUAL_PER_MONTH_EUR[tier] ?? 0)
    : (PRICE_EUR[tier] ?? 0);
}

/** Ce que l'engagement annuel fait économiser sur douze mois. */
export function annualSavingEur(tier) {
  return Math.round(((PRICE_EUR[tier] ?? 0) * 12 - (PRICE_ANNUAL_EUR[tier] ?? 0)) * 100) / 100;
}

/**
 * Le prix affiché en vue annuelle est-il bien le douzième du montant
 * débité ? Utilisé par les tests. Il existe parce que ces deux nombres
 * sont saisis à la main, dans deux buts différents.
 */
export function annualPricesAreCoherent() {
  return TIERS.every((t) => {
    const expected = Math.round(((PRICE_ANNUAL_EUR[t] ?? 0) / 12) * 100) / 100;
    return Math.abs(expected - (PRICE_ANNUAL_PER_MONTH_EUR[t] ?? 0)) <= 0.01;
  });
}

/**
 * Format COMPACT — « 0€ », « 34€ », « 408€ », « 9,99€ ».
 *
 * C'est l'écriture des cartes de /plans, identique dans les trois langues
 * (la référence design l'écrit ainsi). `formatEur` reste l'écriture des
 * surfaces applicatives (« 24,99 € », espace insécable et deux décimales
 * toujours) — deux conventions, deux fonctions, une seule source de
 * chiffres.
 */
export function formatEurCompact(n) {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? `${v}€` : `${v.toFixed(2).replace('.', ',')}€`;
}

/**
 * Format d'un PRIX DE FORMULE — « 0 € », « 39 € », « 34 € », « 9,99 € ».
 *
 * Même typographie que `formatEur` (virgule et « € » en suffixe en
 * français, point et « € » en préfixe ailleurs) MAIS sans les deux
 * décimales imposées : un abonnement à 39 € s'écrit « 39 € », jamais
 * « 39,00 € ». Les décimales sont là pour les montants débités, où le
 * centime compte (`formatEur`) ; sur une grille de formules, elles ne
 * font qu'alourdir un chiffre qui est toujours rond.
 *
 * Troisième écriture, donc, et c'est assumé : /plans écrit compact
 * (« 39€ », la référence design), l'inscription écrit à la française
 * (« 39 € / mois »), les surfaces applicatives écrivent au centime.
 * Trois écritures, un seul jeu de chiffres — celui du dessus.
 */
export function formatEurPlan(n, lang) {
  const v = Number(n) || 0;
  const isFr = String(lang || 'fr').toLowerCase().startsWith('fr');
  if (Number.isInteger(v)) return isFr ? `${v} €` : `€${v}`;
  return isFr ? `${v.toFixed(2).replace('.', ',')} €` : `€${v.toFixed(2)}`;
}


// ─────────────────────────────────────────────────────────────────────
// i18n — phrase d'identité, héritage, puces
// ─────────────────────────────────────────────────────────────────────
export const IDENTITY_COPY = {
  fr: {
    free:    'Tu sais où tu en es.',
    starter: 'Un juriste se bat pour toi.',
    pro:     'Ton bureau juridique complet.',
  },
  en: {
    free:    'You know where you stand.',
    starter: 'A jurist fights for you.',
    pro:     'Your full legal desk.',
  },
  nl: {
    free:    'Je weet waar je staat.',
    starter: 'Een jurist vecht voor jou.',
    pro:     'Je volledige juridische bureau.',
  },
};

// Starter hérite de Free, comme Pro hérite de Starter. Son `null` datait
// de l'époque où sa liste répétait les lignes du Free : elle ne les
// répète plus (cf. FEATURES), donc l'en-tête qui l'explique n'est plus
// facultatif — sans lui, la carte listerait quatre promesses en laissant
// croire que ce sont les seules.
export const INHERITANCE_COPY = {
  fr: { starter: 'Tout Free, plus :', pro: 'Tout Starter, plus :' },
  en: { starter: 'Everything in Free, plus:', pro: 'Everything in Starter, plus:' },
  nl: { starter: 'Alles van Free, plus:', pro: 'Alles van Starter, plus:' },
};

// Les puces respectent l'héritage : chaque palier ne liste QUE ce qu'il
// ajoute au précédent. Elles décrivent ce que le produit fait
// aujourd'hui — pas une feuille de route.
export const FEATURES = {
  fr: {
    free: [
      '2 analyses complètes / mois (litiges et contrats)',
      'Score, fourchette financière, stratégie et lettre',
      'Tes échéances suivies, avec alerte avant qu\'il soit trop tard',
      'Analyse supplémentaire à 3 €',
    ],
    starter: [
      '1 dossier juriste actif — il rédige, appelle, négocie',
      '3 analyses complètes / mois (litiges et contrats)',
      'Lettres rédigées et envoyées par Archer',
      'Recommandé électronique à 9,99 €',
    ],
    pro: [
      '2 dossiers juristes menés de front',
      '4 analyses complètes / mois',
      '1 appel juriste / mois',
      'Toute ta famille couverte — jusqu\'à 3 personnes, quota partagé',
      'Ton juriste répond avant les autres dossiers',
    ],
  },
  en: {
    free: [
      '2 full analyses / month (disputes and contracts)',
      'Score, financial range, strategy and letter',
      'Your deadlines tracked, with an alert before it\'s too late',
      'Extra analysis for €3',
    ],
    starter: [
      '1 active jurist case — they draft, call, negotiate',
      '3 full analyses / month (disputes and contracts)',
      'Letters written and sent by Archer',
      'Electronic registered mail for €9.99',
    ],
    pro: [
      '2 jurist cases run at once',
      '4 full analyses / month',
      '1 jurist call / month',
      'Your whole family covered — up to 3 people, shared quota',
      'Your jurist answers you before the other cases',
    ],
  },
  nl: {
    free: [
      '2 volledige analyses / maand (geschillen en contracten)',
      'Score, financiële bandbreedte, strategie en brief',
      'Je termijnen opgevolgd, met een waarschuwing vóór het te laat is',
      'Extra analyse voor 3 €',
    ],
    starter: [
      '1 actief juristendossier — hij schrijft, belt, onderhandelt',
      '3 volledige analyses / maand (geschillen en contracten)',
      'Brieven geschreven en verstuurd door Archer',
      'Elektronisch aangetekend voor 9,99 €',
    ],
    pro: [
      '2 juristendossiers tegelijk',
      '4 volledige analyses / maand',
      '1 telefoongesprek met je jurist / maand',
      'Je hele gezin gedekt — tot 3 personen, gedeeld quotum',
      'Je jurist antwoordt jou vóór de andere dossiers',
    ],
  },
};


// ─────────────────────────────────────────────────────────────────────
// Petits utilitaires partagés par les deux surfaces
// ─────────────────────────────────────────────────────────────────────

/** Locale-aware EUR price formatter. FR uses a comma decimal + trailing €. */
export function formatEur(n, lang) {
  const isFr = String(lang || 'fr').toLowerCase().startsWith('fr');
  if (!n) return isFr ? '0 €' : '€0';
  return isFr ? `${n.toFixed(2).replace('.', ',')} €` : `€${n.toFixed(2)}`;
}

/** Two-letter code (fr | en | nl) derived from `archer_locale`, safe for SSR. */
export function pickPlansLang() {
  try {
    const loc = (typeof window !== 'undefined' && localStorage.getItem('archer_locale')) || 'fr-be';
    const short = String(loc).split('-')[1] || String(loc).slice(0, 2);
    const s = short.toLowerCase();
    if (s === 'fr' || s === 'nl' || s === 'en') return s;
  } catch (_) { /* SSR safety */ }
  return 'fr';
}

/**
 * Normalise n'importe quelle entrée (backend, cookie, prop) en un palier.
 *
 * `ultimate` retombe sur `pro`, comme la migration de démarrage côté
 * serveur : un compte de test resté sur l'ancien palier doit voir le
 * plan qu'il a réellement, pas « Gratuit ».
 */
export function normalizeTier(raw) {
  const t = String(raw || 'free').toLowerCase();
  if (t === 'solo') return 'starter';     // ancien nom de starter
  if (t === 'ultimate') return 'pro';     // palier retiré de la grille
  if (TIERS.includes(t)) return t;
  return 'free';
}
